import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { AnnualReportProvider } from '@modeso/aumico-lib-annualreport-fe/provider/annualReport.provider';
import { EMPTY_ARRAY_LENGTH, EditSectionTitleComponent } from '@modeso/aumico-lib-core-fe';
import {
    IAnnualReport,
    INotesComponent,
    IPrintOptions,
    ISuperTableComponent,
    ITranslations
} from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { ISubsection, PrintUsingPdfTronDto } from 'projects/aumico-lib-annualreport-fe/src/public-api';
import { NotesAdjustmentsAndReclassesHelper } from 'projects/aumico-project-reports/src/app/microservices/ms-annual-reports/helpers/notesAdjustmentsAndReClasses.helper';
import { PrintPageHelper } from 'projects/aumico-project-reports/src/app/microservices/ms-annual-reports/helpers/printPage.helper';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs';
import { applyReclassificationsToFinStatements } from '../../../../../../aumico-lib-annualreport-fe/src/lib/helpers/applyReclassificationsToFinStatements';
import { NotesNumberingHelper } from '../../../helpers/notes-numbering.helper';
import { NotesEmptyLinesRemovalHelper } from '../../../helpers/notesEmptyLinesRemoval.helper';
import { NotesVisibilityHelper } from '../../../helpers/notesVisibility.helper';
import { INotesSubSectionPrintPreview } from '../../../interfaces/notes-subsection-print-preview.interface';
import { NotesDisplayMapper } from '../../../mappers/notes-display.mapper';
import { FX_NOTES_PRINT_ENGINE, IFxNotesPrintEngine, IFxSuperTablePrintContext } from '../../../interfaces/IFxNotesContext.interface';
import { SuperTableMapper } from '../../../mappers/super-table.mapper';
const debug = Debug('aumico:aumico-lib-annualreport-fe:CashFlowStatementDisplayComponent');
const SPACE = ' ';
/**
 * Data passed to the component will be mutated
 */
@UntilDestroy()
@Component({
    selector: 'aumico-notes-swiss-display',
    templateUrl: 'notes-swiss-display.component.html',
    styleUrls: ['notes-swiss-display.component.scss'],
})
export class NotesSwissDisplayComponent implements OnInit {
    private _printOptions: IPrintOptions | undefined;
    private _annualReport: IAnnualReport;
    private _annualReportForAutofill: IAnnualReport | undefined;
    get printOptions(): IPrintOptions | undefined {
        return this._printOptions;
    }
    @Input() set printOptions(options: IPrintOptions | undefined) {
        this._printOptions = options;
        if (this._annualReport) {
            this.renderNotesSections(this._annualReport, this._annualReportForAutofill);
        }
    }

    private _translations: ITranslations | undefined;
    get translations(): ITranslations | undefined {
        return this._translations;
    }
    /** The print page applies a run in memory before it is saved; without this the super-table would
     * only pick the block up after a reload. A new object on every merge triggers the re-render. */
    @Input() set translations(block: ITranslations | undefined) {
        this._translations = block;
        if (this._annualReport) {
            this.renderNotesSections(this._annualReport, this._annualReportForAutofill);
        }
    }

    @Input() noPreviousData: boolean;

    @Input() currentActiveLocale: string | undefined;
    @Input() reportLocale: string;

    @Input() textColor: string;

    @Output() sectionTitleChanged: EventEmitter<string> = new EventEmitter<string>();

    backgroundColor: string;
    showRatios: boolean;
    showNotes: boolean;
    private pdfTronsubSections: ISubsection[] = [];
    notesSubsections: INotesSubSectionPrintPreview[] = [];
    sectionTitle: string;

    constructor(
        private readonly annualReportProvider: AnnualReportProvider,
        private readonly notesDisplayMapper: NotesDisplayMapper,
        private readonly notesEmptyLinesRemovalHelper: NotesEmptyLinesRemovalHelper,
        private readonly notesNumberingHelper: NotesNumberingHelper,
        private readonly notesVisibilityHelper: NotesVisibilityHelper,
        private readonly notesAdjustmentsAndReclassesHelper: NotesAdjustmentsAndReclassesHelper,
        private readonly printPageHelper: PrintPageHelper,
        private readonly superTableMapper: SuperTableMapper,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private dialog: MatDialog,
        /**
         * FX (REV-21512): app-provided engine facade — the component derives the super-table
         * print FX bundle itself from its own annualReport (reclass self-service pattern).
         * Optional: without a provider (tests, storybook) everything renders legacy (G0).
         */
        @Optional() @Inject(FX_NOTES_PRINT_ENGINE) private readonly fxNotesPrintEngine: IFxNotesPrintEngine | null,
    ) {
        this.addSvgIcon('aumico-pattern-icon-edit-write');
    }

    ngOnInit(): void {
        this.annualReportProvider
            .getPrintView$()
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                filter((printViewData: PrintUsingPdfTronDto) => !!printViewData),
                map((printViewData: PrintUsingPdfTronDto) => printViewData.data.nts),

                withLatestFrom(
                    this.annualReportProvider.getActiveAnnualReportFromCache$(),
                    this.annualReportProvider.getAnnualReportForBalanceAutoFillFromCache$(),
                ),

                untilDestroyed(this),
            )
            .subscribe(([ntsSection, annualReport, annualReportForAutofill]) => {
                this.sectionTitle = ntsSection?.title;
                this.pdfTronsubSections = ntsSection?.subsections ?? [];
                this._annualReport = annualReport;
                this._annualReportForAutofill = annualReportForAutofill;
                this.renderNotesSections(annualReport, annualReportForAutofill);
            });
    }

    private renderNotesSections(annualReport: IAnnualReport, annualReportForAutofill?: IAnnualReport) {
        this.notesSubsections = [];
        let index = -1;

        // REV-20899: build mapping trees from a reclass-applied clone of the
        // current AR's finStatements so the super-table print preview can
        // re-derive each FLI / SubFLI / account row value live. The trees feed
        // `NotesDisplayMapper.mapNotesSuperTableToPrintPreviewData` which
        // overrides the persisted `row.currentValue` / `row.previousValue`
        // when the row is tied to mapping items — necessary because phantom
        // reclasses accounts and post-save reclassifications would otherwise
        // be invisible in the saved snapshot.
        const fxSuperTablePrint = this.fxNotesPrintEngine?.buildSuperTablePrintFx(annualReport) ?? null;
        const currentYearTrees = this.superTableMapper.buildLiveTrees(annualReport, fxSuperTablePrint);
        const previousYearTrees = annualReportForAutofill
            ? this.superTableMapper.buildLiveTrees(annualReportForAutofill, null)
            : null;

        // REV-20899: derive reclasses on a deep clone of the active AR's
        // finStatements before building the FLI-to-accounts map, mirroring
        // SuperTableMapper.buildLiveTrees. Without this, transactional-reclass reports
        // (reclassificationsVersion === 1) feed RAW balances to
        // notesAdjustmentsAndReclassesHelper so the reclass values show as
        // zero/stale. No-op for v0 reports.
        const currentYearReclassedFinStatements = annualReport.finStatements.map((fs) => cloneDeep(fs));
        applyReclassificationsToFinStatements(currentYearReclassedFinStatements, annualReport.groupingAndMasking);

        // I need to apply reclasses here first, otherwise the numbering will be wrong in the case for reclasses
        const currentYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(currentYearReclassedFinStatements);

        const previousYearFliIdToAccounts = this.printPageHelper.getFliIdToAccountsForAnnualReport(
            annualReportForAutofill?.fcp
                ? annualReportForAutofill.fcp.outputFinStatements
                : annualReportForAutofill?.finStatements,
        );

        let notesAccordions = this.notesAdjustmentsAndReclassesHelper.applyAdjustMentsAndReclasses(
            annualReport.notes.accordions,
            this.printOptions.displayOptions,
            {
                currentFliIdToAccounts: currentYearFliIdToAccounts,
                previousFliIdToAccounts: previousYearFliIdToAccounts,
            },
        );



        let notesAccordionsWithNumbering = this.notesNumberingHelper.applyNumbering(
            notesAccordions,
            this.currentActiveLocale,
            this.printOptions.displayOptions.hideEmptyLines || this.printOptions.displayOptions.notesHideEmptyLines,
        );

        notesAccordionsWithNumbering = cloneDeep(this.notesVisibilityHelper.removeHiddenNotes(
            [...notesAccordionsWithNumbering],
        ));

        if (this.printOptions.displayOptions.hideEmptyLines || this.printOptions.displayOptions.notesHideEmptyLines) {
            notesAccordionsWithNumbering = this.notesEmptyLinesRemovalHelper.removeEmptyLinesOfNotes(
                [...notesAccordionsWithNumbering],
                this.reportLocale,
            );
        }

        let previousYearSuperTableComponents: INotesComponent[] = [];

        if (
            annualReportForAutofill != null &&
            annualReportForAutofill.notes != null &&
            annualReportForAutofill.notes.accordions.length !== EMPTY_ARRAY_LENGTH
        ) {
            previousYearSuperTableComponents = annualReportForAutofill.notes.accordions
                .flatMap((accordion) => accordion.data) // Flatten the components array
                .filter((component) => component.componentName === 'Super Table');
        }

        for (const notesAccordion of notesAccordionsWithNumbering) {
            index = index + 1;

            const correspondingPdfTronSubSection: ISubsection = cloneDeep<ISubsection>(
                this.pdfTronsubSections.find((subSection) => subSection.sectionId === index),
            );

            if (correspondingPdfTronSubSection == null) {
                continue;
            }

            const notesSubSectionPrintPreview: INotesSubSectionPrintPreview = {
                id: correspondingPdfTronSubSection.sectionId,
                title: correspondingPdfTronSubSection.title,
                components: this.notesDisplayMapper.mapPdfTronSubSectionItemsToNotesSubSectionComponentsForPrintPreview(
                    correspondingPdfTronSubSection.items,
                ),
            };
            notesAccordion.data.forEach((component: INotesComponent, componentIndex: number) => {
                if (component.componentName === 'Super Table') {
                    const superTableData = this.notesDisplayMapper.mapNotesSuperTableToPrintPreviewData(
                        component.data as ISuperTableComponent,
                        annualReport.generalInformation.balanceSheetDate,
                        annualReport.generalInformation.balancePreviousYearDate,
                        this.printOptions.displayOptions,
                        notesSubSectionPrintPreview.components[componentIndex].componentTitle?.[
                        this.currentActiveLocale
                        ],
                        this.currentActiveLocale,
                        component.hasFootnotes || false,
                        annualReport.organizationId?.toString(),
                        currentYearTrees,
                        fxSuperTablePrint?.context ?? null,
                        this._translations?.[this.currentActiveLocale]?.entries,
                        this.reportLocale,
                    );

                    // Add Previous Year Super Table
                    const correspondingPreviousYearSuperTable = previousYearSuperTableComponents.find(
                        (previousComponent) =>
                            previousComponent.componentId === component.componentId &&
                            (component.data as ISuperTableComponent).options.showPreviousYear,
                    )?.data;

                    if (correspondingPreviousYearSuperTable != null) {
                        superTableData.previousYear = this.notesDisplayMapper.mapNotesSuperTableToPrintPreviewData(
                            correspondingPreviousYearSuperTable,
                            annualReportForAutofill.generalInformation.balanceSheetDate,
                            annualReportForAutofill.generalInformation.balancePreviousYearDate,
                            this.printOptions.displayOptions,
                            undefined,
                            this.currentActiveLocale,
                            component.hasFootnotes || false,
                            annualReportForAutofill.organizationId?.toString(),
                            previousYearTrees ?? undefined,
                            null,
                            annualReportForAutofill.translations?.[this.currentActiveLocale]?.entries,
                            this.reportLocale,
                        );
                    }
                    // this is to identify data that already saved with repeated component id https://modeso.atlassian.net/browse/REV-17556
                    const componentIdWithIndex = `${component.componentId}_${componentIndex}`;

                    let correspondingNotesSubsectionComponent = notesSubSectionPrintPreview.components.find(
                        (comp) => comp.componentId === componentIdWithIndex,
                    );

                    if (
                        correspondingNotesSubsectionComponent != null &&
                        correspondingNotesSubsectionComponent.componentType === 'ST'
                    ) {
                        notesSubSectionPrintPreview.components[componentIndex] = {
                            ...notesSubSectionPrintPreview.components[componentIndex],
                            data: superTableData,
                        };
                    }
                }
            });
            this.notesSubsections.push(notesSubSectionPrintPreview);
        }
    }

    public editSectionTitle(): void {
        const dialogRef = this.dialog.open<EditSectionTitleComponent, string, string | undefined>(
            EditSectionTitleComponent,
            {
                disableClose: false,
                panelClass: 'custom-modalbox',
                data: this.sectionTitle,
            },
        );

        dialogRef
            .afterClosed()
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                if (result != null) {
                    this.sectionTitle = result;
                    this.sectionTitleChanged.emit(result);
                }
            });
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

}
